import { PageHero } from "@/components/hero/PageHero";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { DOWNLOAD_LINK_HOURS, RETENTION_HOURS } from "@/lib/trustCopy";

/**
 * Plain engineering-facts security page — no badges, no vendor
 * boilerplate. Every statement here describes something the system
 * actually does; if we can't state it as a fact, it doesn't go on
 * this page.
 */
export function Security() {
  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <Header />
      <main className="flex-1">
        <PageHero
          eyebrow="Security"
          title="How your data is protected."
          subtitle="The engineering facts — what we encrypt, where data lives, and when it is deleted."
          size="sm"
        />
        <section className="bg-bg py-20">
          <div className="wrap max-w-3xl space-y-6 text-sm leading-relaxed text-muted">
            <h2 className="font-heading text-lg font-bold text-ink">Encryption</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>All traffic is encrypted in transit with TLS.</li>
              <li>
                Uploaded documents, drafts, and exports are stored in object storage that encrypts
                everything at rest.
              </li>
            </ul>

            <h2 className="font-heading text-lg font-bold text-ink">Where your data lives</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Your documents and the health information extracted from them never enter our
                database. The database holds public coverage policies (CMS guidelines, payer
                policies, clinical guidelines) plus operational records like consent receipts —
                not your medical data.
              </li>
              <li>
                Uploads, drafts, and session transcripts live in access-controlled object storage
                under a key only your session can reach, and are removed by the retention sweep
                below.
              </li>
              <li>
                Session transcripts are scrubbed before they are written: Social Security and
                labeled member/record numbers, dates of birth, phone numbers, and email addresses
                are redacted.
              </li>
            </ul>

            <h2 className="font-heading text-lg font-bold text-ink">Retention — the actual windows</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Everything tied to your session — uploads, extracted data, the drafted appeal, and
                the transcript — is deleted automatically within {RETENTION_HOURS} hours, on a
                fixed schedule.
              </li>
              <li>
                Paid-appeal download links expire {DOWNLOAD_LINK_HOURS} hours after the delivery
                email is sent.
              </li>
              <li>
                What persists longer: your legal-consent receipt, anonymized outcome feedback (no
                documents, no identifying details), and the email you gave at checkout for
                delivering your purchase.
              </li>
            </ul>

            <h2 className="font-heading text-lg font-bold text-ink">Accounts and payments</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                No accounts, no passwords — by design. Your session exists in your browser tab;
                there is no stored profile to breach.
              </li>
              <li>
                Payments are processed by Stripe. Your card number never touches our servers.
              </li>
            </ul>

            <h2 className="font-heading text-lg font-bold text-ink">AI providers</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                The AI services we use to read and draft are contractually barred from training on,
                retaining, or reusing your content.
              </li>
              <li>
                We don&apos;t train on your documents either. The only thing we learn from is
                anonymized outcome data — whether an appeal succeeded — which contains no documents
                and no identifying details.
              </li>
            </ul>

            <h2 className="font-heading text-lg font-bold text-ink">What we don&apos;t claim</h2>
            <p>
              We&apos;re a small team and haven&apos;t completed a formal certification such as
              SOC 2 yet. Rather than display badges we haven&apos;t earned, this page states
              exactly what the system does. If a security incident ever affected your data, we
              would notify affected users without undue delay, as required by law. Questions or
              something to report? Contact us through the{" "}
              <a href="/help" className="font-medium text-ink underline">
                help center
              </a>
              .
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
